import { ManagePageColumnT } from "../../models/models";

// spec-13 Task 7.1 — resolve-by-name order-grid query builder.
//
// Replaces the family of `index === N` builders (buildDeliveryOrdersQueryStringWithClientIdSearch's
// hardcoded `index === 13`, etc.) for the two GlobalAdmin order grids. Instead of guessing a column's
// position, it places each filter's value on the column whose `value` equals the backend field name
// that `OrderGridFilters` (BL) matches on: OrderStatus, EmailAddress, ProjectDeliveryNumber,
// InvoiceName, ContactName, CompanyName, ClientId. Because it matches BY NAME, it is immune to column
// reordering/slimming (Task 7.4) — the old builders silently targeted the wrong field the moment the
// columns array changed.
//
// Contract notes (verified against DataTablesBinder.GetColumns + OrderGridFilters.GetColumnSearchValue):
//  - Every column emits BOTH `columns[i][data]` and `columns[i][name]`, contiguously from 0. The binder
//    stops at the first index missing either key, so a gap would drop every later column.
//  - `searchable=true` is emitted for any column carrying a filter value (§9.1 — no order column sets
//    `isSearchable`, so the old builders shipped `searchable=false` for everything). The binder parses
//    the search value regardless of the flag, but emitting it true keeps the request honest.
//  - A filter whose field is NOT a visible column (possible after Task 7.4 slims the grid) is appended
//    as a synthetic trailing column — OrderGridFilters matches by `data` name, not by visibility, so the
//    value still reaches the backend.
//  - Filter values are URL-encoded (email / company name are free text); "null"/"NaN"/blank are dropped
//    (the FE sends those for an unset control; the backend already treats them as absent).
//  - The GLOBAL search term is URL-encoded too (spec-31). It used to be interpolated raw, so a term
//    containing "&" or "#" silently truncated at the backend. ASP.NET decodes on the way in, so plain
//    terms are byte-identical to before.
export type OrderGridFiltersT = Record<string, string | number | null | undefined>;

const emitColumnParam = (
  index: number,
  data: string,
  searchable: boolean,
  searchValue: string
) =>
  `&columns[${index}][data]=${data}&columns[${index}][name]=&columns[${index}][searchable]=${Boolean(
    searchable
  )}&columns[${index}][orderable]=true&columns[${index}][search][value]=${searchValue}&columns[${index}][search][regex]=false`;

export const buildOrderGridQueryString = (
  columns: ManagePageColumnT[],
  page: number,
  searchTerm: string | number,
  start: number,
  limit: number,
  orderColumnIndex: number,
  isDescSortDir: boolean,
  filters: OrderGridFiltersT = {}
) => {
  // Keep only real filter values; the backend reads "null"/"NaN" as absent, and shipping them just
  // wastes a column slot and reads as a smell.
  const activeFilters: Record<string, string> = {};
  Object.keys(filters || {}).forEach((key) => {
    const raw = filters[key];
    if (raw === null || raw === undefined) return;
    const val = String(raw).trim();
    if (val === "" || val === "null" || val === "NaN" || val === "undefined") return;
    activeFilters[key] = val;
  });

  const columnList = columns || [];
  const usedKeys = new Set<string>();

  // Visible columns, contiguous from 0. A filter lands on the column whose `value` matches BY NAME —
  // this is what replaces `index === 13`.
  const visible = columnList.map((column, index) => {
    const hasFilter = Object.prototype.hasOwnProperty.call(
      activeFilters,
      column.value
    );
    if (hasFilter) usedKeys.add(column.value);
    const searchValue = hasFilter
      ? encodeURIComponent(activeFilters[column.value])
      : "";
    const searchable = hasFilter || Boolean(column.isSearchable);
    return emitColumnParam(index, column.value, searchable, searchValue);
  });

  // Filters with no matching visible column still need to reach the backend (it matches by `data`
  // name). Append them as synthetic, contiguous columns after the visible ones.
  const synthetic = Object.keys(activeFilters)
    .filter((key) => !usedKeys.has(key))
    .map((key, i) =>
      emitColumnParam(
        columnList.length + i,
        key,
        true,
        encodeURIComponent(activeFilters[key])
      )
    );

  const queryParamColumns = [...visible, ...synthetic].join("");

  return `?draw=${page}&start=${start}&length=${limit}&search[value]=${encodeURIComponent(
    searchTerm ?? ""
  )}&search[regex]=false${queryParamColumns}&order[0][column]=${
    orderColumnIndex || 0
  }&order[0][dir]=${isDescSortDir ? "desc" : "asc"}`;
};

export const buildManagePageQueryString = (
  columns: ManagePageColumnT[],
  page: number,
  searchTerm: string | number,
  start: number,
  limit: number,
  orderColumnIndex: number,
  isDescSortDir: boolean
) => {
  // get query params for each column
  const queryParamColumns = columns?.map((column, index) => {
    return `&columns[${index}][data]=${
      column.value
    }&columns[${index}][name]=&columns[${index}][searchable]=${Boolean(
      column.isSearchable
    )}&columns[${index}][orderable]=true&columns[${index}][search][value]=&columns[${index}][search][regex]=false`;
  });

  return `?draw=${page}&start=${start}&length=${limit}&search[value]=${searchTerm}&search[regex]=false${queryParamColumns.join(
    ""
  )}&order[0][column]=${orderColumnIndex || 0}&order[0][dir]=${
    isDescSortDir ? "desc" : "asc"
  }`;
};

export const buildManagePageQueryStringWithStatusFilter = (
  columns: ManagePageColumnT[],
  page: number,
  searchTerm: string | number,
  start: number,
  limit: number,
  orderColumnIndex: number,
  isDescSortDir: boolean,
  status: number
) => {
  // get query params for each column
  const queryParamColumns = columns?.map((column, index) => {
    return `&columns[${index}][data]=${
      column.value
    }&columns[${index}][name]=&columns[${index}][searchable]=${Boolean(
      column.isSearchable || index === 10
    )}&columns[${index}][orderable]=true&columns[${index}][search][value]=${
      index === 10 ? status : ""
    }&columns[${index}][search][regex]=false`;
  });

  return `?draw=${page}&start=${start}&length=${limit}&search[value]=${searchTerm}&search[regex]=false${queryParamColumns.join(
    ""
  )}&order[0][column]=${orderColumnIndex || 0}&order[0][dir]=${
    isDescSortDir ? "desc" : "asc"
  }`;
};

// search with client type and filter by status
export const buildManagePageQueryStringWithClientTypeSearch = (
  columns: ManagePageColumnT[],
  page: number,
  searchTerm: string | number,
  clientTypeSearchTerm: string | number,
  start: number,
  limit: number,
  orderColumnIndex: number,
  isDescSortDir: boolean,
  status: string
) => {
  // get query params for each column
  const queryParamColumns = columns?.map((column, index) => {
    return `&columns[${index}][data]=${
      column.value
    }&columns[${index}][name]=&columns[${index}][searchable]=${Boolean(
      column.isSearchable
    )}&columns[${index}][orderable]=true&columns[${index}][search][value]=${
      index === 2 ? clientTypeSearchTerm : index === 9 ? status : ""
    }&columns[${index}][search][regex]=false`;
  });

  return `?draw=${page}&start=${start}&length=${limit}&search[value]=${searchTerm}&search[regex]=false${queryParamColumns.join(
    ""
  )}&order[0][column]=${orderColumnIndex || 0}&order[0][dir]=${
    isDescSortDir ? "desc" : "asc"
  }`;
};
export const buildManagePageQueryStringWithServicePackageSearchSearch = (
  columns: ManagePageColumnT[],
  page: number,
  searchTerm: string | number,
  servicePackageSearchTerm: string | number,
  start: number,
  limit: number,
  orderColumnIndex: number,
  isDescSortDir: boolean,
  status: string
) => {
  // get query params for each column
  const queryParamColumns = columns?.map((column, index) => {
    return `&columns[${index}][data]=${
      column.value
    }&columns[${index}][name]=&columns[${index}][searchable]=${Boolean(
      column.isSearchable
    )}&columns[${index}][orderable]=true&columns[${index}][search][value]=${
      index === 7 ? servicePackageSearchTerm : index === 9 ? status : ""
    }&columns[${index}][search][regex]=false`;
  });

  return `?draw=${page}&start=${start}&length=${limit}&search[value]=${searchTerm}&search[regex]=false${queryParamColumns.join(
    ""
  )}&order[0][column]=${orderColumnIndex || 0}&order[0][dir]=${
    isDescSortDir ? "desc" : "asc"
  }`;
};

export const buildDeliveryOrdersQueryStringWithClientIdSearch = (
  columns: ManagePageColumnT[],
  page: number,
  searchTerm: string | number,
  clientIdSearchTerm: string | number,
  start: number,
  limit: number,
  orderColumnIndex: number,
  isDescSortDir: boolean
) => {
  // get query params for each column
  const queryParamColumns = columns?.map((column, index) => {
    return `&columns[${index}][data]=${
      column.value
    }&columns[${index}][name]=&columns[${index}][searchable]=${Boolean(
      column.isSearchable
    )}&columns[${index}][orderable]=true&columns[${index}][search][value]=${
      index === 13 ? clientIdSearchTerm : ""
    }&columns[${index}][search][regex]=false`;
  });

  return `?draw=${page}&start=${start}&length=${limit}&search[value]=${searchTerm}&search[regex]=false${queryParamColumns.join(
    ""
  )}&order[0][column]=${orderColumnIndex || 0}&order[0][dir]=${
    isDescSortDir ? "desc" : "asc"
  }`;
};
